// ============================================================
// subscription-plan.service.ts
// ============================================================

import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  Firestore,
  collection,
  collectionData,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Timestamp } from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────
// Firestore document shape  →  subscription_plans/<docId>
// Fields removed: apiAccess, prioritySupport, analyticsEnabled
// Fields added:   storageLimitGb, listedEquipmentModuleEnabled,
//                 trialVehicleCap, listingExpiryReminderEnabled,
//                 listingExpiryReminderDays
// ─────────────────────────────────────────────────────────────

export interface FirestorePlan {
  planId: string;
  name: string;
  description: string;
  color: string;
  tag: string;
  icon: string;
  monthlyPrice: number;
  listingPrice: number;
  maxListings: number;
  bookingLimit: number;
  extraBookingEnabled: boolean;
  extraBookingPrice: number;
  extraBookingCount: number;
  marketplaceEnabled: boolean;
  listedEquipmentModuleEnabled: boolean;
  listingDays: number;
  bookingCycleDays: number;
  graceDays: number;
  trialEnabled: boolean;
  trialDays: number;
  trialVehicleCap: number;
  listingExpiryReminderEnabled: boolean;
  listingExpiryReminderDays: number;
  listedEquipmentCap: number;
  storageLimitGb: number;
  quarterlyDiscount: number;
  halfYearlyDiscount: number;
  yearlyDiscount: number;
  archiveMonths: number | null;
  public: boolean;
  active: boolean;
  displayOrder: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ─────────────────────────────────────────────────────────────
// AdminPlan — the shape the component works with
// ─────────────────────────────────────────────────────────────

export interface AdminPlan {
  id: string;
  name: string;
  tagline: string;
  color: string;
  tag: string;
  icon: string;
  monthlyFee: number;
  listingFee: number;
  activeListingLimit: number;
  bookingLimit: number;
  addOnBookingEnabled: boolean;
  addOnBookingPrice: number;
  addOnBookingCount: number;
  marketplaceEnabled: boolean;
  /** When false the entire Listed Equipment module is hidden for this plan */
  listedEquipmentModuleEnabled: boolean;
  /** Limit on number of vehicles listed in Listed Equipment, 0 = Unlimited */
  listedEquipmentCap: number;
  listingDurationDays: number;
  manualBookingCycleDays: number;
  gracePeriodDays: number;
  trialEnabled: boolean;
  trialDays: number;
  /** Max vehicles a vendor may trial-list; prevents abuse during free trial */
  trialVehicleCap: number;
  /** Send an expiry reminder to vendors N days before a listing expires */
  listingExpiryReminderEnabled: boolean;
  listingExpiryReminderDays: number;
  /** Storage quota in GB (relevant for dashboard-only plans) */
  storageLimitGb: number;
  quarterlyDiscount: number;
  halfYearlyDiscount: number;
  yearlyDiscount: number;
  documentArchiveMonths: number;
  isActive: boolean;
  subscriberCount: number;
  lastUpdated: Date;
  createdAt: Date;
}

// Only the fields the banner needs — nothing else
export interface PlatformOfferDoc {
  enabled: boolean;
  badgeText: string;
  highlightText: string;
  bodyText: string;
  countdownLabel: string;
  expiryDate: string;
}

export interface BillingTerm {
  id: string;
  label: string;
  months: number;
  discount: number;
}

export interface FeatureRow {
  label: string;
  tooltip?: string;
  cells: { [planId: string]: boolean | string };
}

export interface FeatureSection {
  title: string;
  icon: string;
  rows: FeatureRow[];
}

@Injectable({ providedIn: 'root' })
export class SubscriptionPlanService {

  private readonly COLLECTION = 'subscription_plans';
  private readonly OFFER_DOC_ID = 'offer';

  readonly plans$ = new BehaviorSubject<AdminPlan[]>([]);

  readonly terms: BillingTerm[] = [
    { id: 'monthly', label: 'Monthly', months: 1, discount: 0 },
    { id: 'quarterly', label: 'Quarterly', months: 3, discount: 5 },
    { id: 'halfYearly', label: 'Half-Yearly', months: 6, discount: 10 },
    { id: 'yearly', label: 'Yearly', months: 12, discount: 15 },
  ];

  featureSections: FeatureSection[] = [];

  constructor(
    private firestore: Firestore,
    private auth: Auth,
  ) {
    this.getPlans().subscribe(plans => {
      this.plans$.next(plans);
      this.featureSections = this.buildFeatureSections(plans);
    });
  }

  // ── Ref helper ───────────────────────────────────────────────

  private get offerDocRef() {
    return doc(this.firestore, this.COLLECTION, this.OFFER_DOC_ID);
  }

  // ── READ ────────────────────────────────────────────────────

  getPlans(): Observable<AdminPlan[]> {
    const plansRef = collection(this.firestore, this.COLLECTION);
    return (collectionData(plansRef, { idField: 'docId' }) as Observable<any[]>).pipe(
      map(docs =>
        docs
          .filter(d => d.docId !== this.OFFER_DOC_ID)
          .map(d => this.fromFirestore(d))
          .sort((a, b) => a.name.localeCompare(b.name))
      )
    );
  }

  // ── CREATE ──────────────────────────────────────────────────

  async createPlan(
    plan: Omit<AdminPlan, 'id' | 'subscriberCount' | 'lastUpdated' | 'createdAt'>
  ): Promise<void> {
    const plansRef = collection(this.firestore, this.COLLECTION);
    await addDoc(plansRef, {
      ...this.toFirestore(plan, ''),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  // ── UPDATE ──────────────────────────────────────────────────

  async updatePlan(id: string, changes: Partial<AdminPlan>): Promise<void> {
    const planRef = doc(this.firestore, this.COLLECTION, id);
    await updateDoc(planRef, {
      ...this.partialToFirestore(changes),
      updatedAt: serverTimestamp(),
    });
  }

  // ── DELETE ──────────────────────────────────────────────────

  async deletePlan(id: string): Promise<void> {
    await deleteDoc(doc(this.firestore, this.COLLECTION, id));
  }

  // ── PLATFORM OFFER ──────────────────────────────────────────

  async getPlatformOffer(): Promise<PlatformOfferDoc | null> {
    const snap = await getDoc(this.offerDocRef);
    if (!snap.exists()) return null;
    return snap.data() as PlatformOfferDoc;
  }

  async savePlatformOffer(offer: PlatformOfferDoc): Promise<void> {
    const clean: PlatformOfferDoc = {
      enabled: offer.enabled,
      badgeText: offer.badgeText,
      highlightText: offer.highlightText,
      bodyText: offer.bodyText,
      countdownLabel: offer.countdownLabel,
      expiryDate: offer.expiryDate,
    };
    await setDoc(this.offerDocRef, clean);
  }

  // ── FEATURE TABLE ───────────────────────────────────────────

  buildFeatureSections(plans: AdminPlan[]): FeatureSection[] {
    const cells = (
      fn: (pl: AdminPlan) => string | boolean
    ): { [planId: string]: string | boolean } =>
      plans.reduce((acc, pl) => { acc[pl.id] = fn(pl); return acc; }, {} as any);

    return [
      {
        title: 'Pricing', icon: 'fa-tag',
        rows: [
          { label: 'Monthly Platform Fee (AED)', cells: cells(pl => pl.monthlyFee === 0 ? 'Free' : String(pl.monthlyFee)) },
          { label: 'Quarterly Discount', cells: cells(pl => pl.monthlyFee === 0 ? 'N/A' : pl.quarterlyDiscount + '%') },
          { label: 'Half-Yearly Discount', cells: cells(pl => pl.monthlyFee === 0 ? 'N/A' : pl.halfYearlyDiscount + '%') },
          { label: 'Yearly Discount', cells: cells(pl => pl.monthlyFee === 0 ? 'N/A' : pl.yearlyDiscount + '%') },
        ],
      },
      {
        title: 'Marketplace & Listings', icon: 'fa-store',
        rows: [
          { label: 'Listed Equipment Module', cells: cells(pl => pl.listedEquipmentModuleEnabled) },
          { label: 'Listed Equipment Cap', cells: cells(pl => !pl.listedEquipmentModuleEnabled ? 'N/A' : pl.listedEquipmentCap === 0 ? 'Unlimited' : String(pl.listedEquipmentCap)) },
          { label: 'Marketplace Access', cells: cells(pl => pl.marketplaceEnabled) },
          { label: 'Max Active Listings', cells: cells(pl => !pl.marketplaceEnabled ? 'N/A' : pl.activeListingLimit === 0 ? 'Unlimited' : String(pl.activeListingLimit)) },
          { label: 'Listing Fee per Vehicle (AED/30d)', cells: cells(pl => !pl.marketplaceEnabled ? 'N/A' : pl.listingFee === 0 ? 'Free' : String(pl.listingFee)) },
          { label: 'Listing Duration (days)', cells: cells(pl => !pl.marketplaceEnabled ? 'N/A' : String(pl.listingDurationDays)) },
          { label: 'Expiry Reminder', cells: cells(pl => pl.listingExpiryReminderEnabled ? pl.listingExpiryReminderDays + 'd before' : false) },
        ],
      },
      {
        title: 'Bookings', icon: 'fa-calendar-check',
        rows: [
          { label: 'Manual Bookings / Cycle', cells: cells(pl => pl.bookingLimit === 0 ? 'Unlimited' : String(pl.bookingLimit)) },
          { label: 'Add-On Booking Packs', cells: cells(pl => pl.addOnBookingEnabled) },
          { label: 'Booking Cycle (days)', cells: cells(pl => String(pl.manualBookingCycleDays)) },
          { label: 'Grace Period (days)', cells: cells(pl => pl.monthlyFee === 0 ? 'N/A' : String(pl.gracePeriodDays)) },
        ],
      },
      {
        title: 'Trial & Storage', icon: 'fa-clock',
        rows: [
          { label: 'Free Trial', cells: cells(pl => pl.trialEnabled ? pl.trialDays + ' days' : false) },
          { label: 'Trial Vehicle Cap', cells: cells(pl => pl.trialEnabled ? String(pl.trialVehicleCap) : 'N/A') },
          { label: 'Storage Limit', cells: cells(pl => pl.storageLimitGb === 0 ? 'Unlimited' : pl.storageLimitGb + ' GB') },
          { label: 'Document Archive', cells: cells(pl => pl.documentArchiveMonths === 999 ? 'Unlimited' : pl.documentArchiveMonths + ' months') },
        ],
      },
    ];
  }

  // ── FIRESTORE → ADMINPLAN ───────────────────────────────────

  private fromFirestore(data: any): AdminPlan {
    return {
      id: data.docId,
      name: data.name ?? '',
      tagline: data.description ?? '',
      color: data.color ?? '#6366f1',
      tag: data.tag ?? '',
      icon: data.icon ?? 'fa-box',
      monthlyFee: data.monthlyPrice ?? 0,
      listingFee: data.listingPrice ?? 0,
      activeListingLimit: data.maxListings ?? 0,
      bookingLimit: data.bookingLimit ?? 0,
      addOnBookingEnabled: data.extraBookingEnabled ?? false,
      addOnBookingPrice: data.extraBookingPrice ?? 0,
      addOnBookingCount: data.extraBookingCount ?? 0,
      marketplaceEnabled: data.marketplaceEnabled ?? false,
      listedEquipmentModuleEnabled: data.listedEquipmentModuleEnabled ?? true,
      listedEquipmentCap: data.listedEquipmentCap ?? 0,
      listingDurationDays: data.listingDays ?? 30,
      manualBookingCycleDays: data.bookingCycleDays ?? 30,
      gracePeriodDays: data.graceDays ?? 5,
      trialEnabled: data.trialEnabled ?? false,
      trialDays: data.trialDays ?? 0,
      trialVehicleCap: data.trialVehicleCap ?? 200,
      listingExpiryReminderEnabled: data.listingExpiryReminderEnabled ?? true,
      listingExpiryReminderDays: data.listingExpiryReminderDays ?? 3,
      storageLimitGb: data.storageLimitGb ?? 0,
      quarterlyDiscount: data.quarterlyDiscount ?? 5,
      halfYearlyDiscount: data.halfYearlyDiscount ?? 10,
      yearlyDiscount: data.yearlyDiscount ?? 15,
      documentArchiveMonths: data.archiveMonths ?? 6,
      isActive: data.active ?? true,
      subscriberCount: 0,
      lastUpdated: data.updatedAt?.toDate?.() ?? new Date(),
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
    };
  }

  // ── ADMINPLAN → FIRESTORE ───────────────────────────────────

  private toFirestore(
    plan: Omit<AdminPlan, 'id' | 'subscriberCount' | 'lastUpdated' | 'createdAt'>,
    planId: string
  ): Omit<FirestorePlan, 'createdAt' | 'updatedAt'> {
    return {
      planId,
      name: plan.name,
      description: plan.tagline,
      color: plan.color,
      tag: plan.tag ?? '',
      icon: plan.icon ?? 'fa-box',
      monthlyPrice: plan.monthlyFee,
      listingPrice: plan.listingFee,
      maxListings: plan.activeListingLimit,
      bookingLimit: plan.bookingLimit,
      extraBookingEnabled: plan.addOnBookingEnabled,
      extraBookingPrice: plan.addOnBookingPrice,
      extraBookingCount: plan.addOnBookingCount,
      marketplaceEnabled: plan.marketplaceEnabled,
      listedEquipmentModuleEnabled: plan.listedEquipmentModuleEnabled,
      listedEquipmentCap: plan.listedEquipmentCap ?? 0,
      listingDays: plan.listingDurationDays,
      bookingCycleDays: plan.manualBookingCycleDays,
      graceDays: plan.gracePeriodDays,
      trialEnabled: plan.trialEnabled,
      trialDays: plan.trialDays,
      trialVehicleCap: plan.trialVehicleCap,
      listingExpiryReminderEnabled: plan.listingExpiryReminderEnabled,
      listingExpiryReminderDays: plan.listingExpiryReminderDays,
      storageLimitGb: plan.storageLimitGb,
      quarterlyDiscount: plan.quarterlyDiscount ?? 5,
      halfYearlyDiscount: plan.halfYearlyDiscount ?? 10,
      yearlyDiscount: plan.yearlyDiscount ?? 15,
      archiveMonths: plan.documentArchiveMonths,
      public: true,
      active: plan.isActive ?? true,
      displayOrder: 0,
    };
  }

  // ── PARTIAL UPDATE MAPPER ───────────────────────────────────

  private partialToFirestore(changes: Partial<AdminPlan>): Partial<FirestorePlan> {
    const r: Partial<FirestorePlan> = {};
    if (changes.name !== undefined) r.name = changes.name;
    if (changes.tagline !== undefined) r.description = changes.tagline;
    if (changes.color !== undefined) r.color = changes.color;
    if (changes.tag !== undefined) r.tag = changes.tag;
    if (changes.icon !== undefined) r.icon = changes.icon;
    if (changes.monthlyFee !== undefined) r.monthlyPrice = changes.monthlyFee;
    if (changes.listingFee !== undefined) r.listingPrice = changes.listingFee;
    if (changes.activeListingLimit !== undefined) r.maxListings = changes.activeListingLimit;
    if (changes.bookingLimit !== undefined) r.bookingLimit = changes.bookingLimit;
    if (changes.addOnBookingEnabled !== undefined) r.extraBookingEnabled = changes.addOnBookingEnabled;
    if (changes.addOnBookingPrice !== undefined) r.extraBookingPrice = changes.addOnBookingPrice;
    if (changes.addOnBookingCount !== undefined) r.extraBookingCount = changes.addOnBookingCount;
    if (changes.marketplaceEnabled !== undefined) r.marketplaceEnabled = changes.marketplaceEnabled;
    if (changes.listedEquipmentModuleEnabled !== undefined) r.listedEquipmentModuleEnabled = changes.listedEquipmentModuleEnabled;
    if (changes.listedEquipmentCap !== undefined) r.listedEquipmentCap = changes.listedEquipmentCap;
    if (changes.listingDurationDays !== undefined) r.listingDays = changes.listingDurationDays;
    if (changes.manualBookingCycleDays !== undefined) r.bookingCycleDays = changes.manualBookingCycleDays;
    if (changes.gracePeriodDays !== undefined) r.graceDays = changes.gracePeriodDays;
    if (changes.trialEnabled !== undefined) r.trialEnabled = changes.trialEnabled;
    if (changes.trialDays !== undefined) r.trialDays = changes.trialDays;
    if (changes.trialVehicleCap !== undefined) r.trialVehicleCap = changes.trialVehicleCap;
    if (changes.listingExpiryReminderEnabled !== undefined) r.listingExpiryReminderEnabled = changes.listingExpiryReminderEnabled;
    if (changes.listingExpiryReminderDays !== undefined) r.listingExpiryReminderDays = changes.listingExpiryReminderDays;
    if (changes.storageLimitGb !== undefined) r.storageLimitGb = changes.storageLimitGb;
    if (changes.quarterlyDiscount !== undefined) r.quarterlyDiscount = changes.quarterlyDiscount;
    if (changes.halfYearlyDiscount !== undefined) r.halfYearlyDiscount = changes.halfYearlyDiscount;
    if (changes.yearlyDiscount !== undefined) r.yearlyDiscount = changes.yearlyDiscount;
    if (changes.documentArchiveMonths !== undefined) r.archiveMonths = changes.documentArchiveMonths;
    if (changes.isActive !== undefined) r.active = changes.isActive;
    return r;
  }

  async getWorkerSettings() {
    const docRef = doc(this.firestore, 'system_settings', 'worker_management');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data() as { enabled: boolean; defaultWorkerLimit: number };
    }
    return { enabled: true, defaultWorkerLimit: 2 };
  }

  async saveWorkerSettings(settings: { enabled: boolean; defaultWorkerLimit: number }) {
    const docRef = doc(this.firestore, 'system_settings', 'worker_management');
    await setDoc(docRef, settings, { merge: true });
  }

  async getWorkerStats() {
    const usersCol = collection(this.firestore, 'users-list');
    const q = query(usersCol, where('role', '==', 'worker'));
    const snap = await getDocs(q);
    const workerDocs = snap.docs.map(d => d.data());
    const totalWorkers = workerDocs.length;
    const uniqueParents = new Set(workerDocs.map(w => w['parentUid']).filter(Boolean));
    const vendorsCount = uniqueParents.size;
    return { totalWorkers, vendorsCount };
  }
}